import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Landmark } from "lucide-react";

// Tells homeowners whether their live Stripe payout setup is done, before bookings open.
export function HostLivePayoutPrompt() {
  const { data: s } = useQuery({ queryKey: ["public-settings"], queryFn: () => api.get("/settings/public").then((r) => r.data) });
  const { data: acct } = useQuery({ queryKey: ["connect-account"], queryFn: () => api.get("/connect/account").then((r) => r.data), enabled: !!s?.payments_live, retry: false });
  if (!s) return null;
  if (!s.payments_live) {
    return (
      <div className="mb-6 flex gap-3 rounded-2xl border bg-stone-50 p-4 text-sm" data-testid="live-payout-coming">
        <Landmark className="h-5 w-5 shrink-0 text-primary" />
        <p><span className="font-semibold">Live payouts are coming.</span> When FairStay switches on real payments, you'll set up payouts with Stripe here. It takes about 10 minutes and needs your bank account and ID. Test-mode setups don't carry over.</p>
      </div>
    );
  }
  if (!acct) return null;
  if (acct.connected && acct.ready_to_process_payments && acct.onboarding_complete) {
    return <p className="mb-6 flex items-center gap-2 rounded-2xl bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="live-payout-ready"><BadgeCheck className="h-4 w-4" />Live payouts are set up. Guests can pay for your stays once bookings open.</p>;
  }
  if (acct.connected && acct.onboarding_complete) {
    return <p className="mb-6 flex items-center gap-2 rounded-2xl border bg-stone-50 p-3 text-sm text-stone-700" data-testid="live-payout-verifying"><Landmark className="h-4 w-4 text-primary" />Your payout details are in. Stripe is still verifying them, which can take a day or two.</p>;
  }
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="live-payout-required">
      <p><span className="font-semibold">Finish your live payout setup.</span> Guests can't book your home until Stripe can pay you. It takes about 10 minutes with your bank account and ID.</p>
      <Button asChild className="rounded-full" data-testid="live-payout-setup-button"><Link to="/host/payouts">{acct.connected ? "Continue setup" : "Set up payouts"}</Link></Button>
    </div>
  );
}
